import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { AbstractComponent } from '../../abstract.component';
import { SolIcon } from '@sol/icons';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { SolButtonComponent } from '../../core/sol-button/sol-button.component';
import { SolTagComponent } from '../../core/sol-tag/sol-tag.component';
import { ShortcutService, SolShortcut, SolShortcutSubscription } from '../../../services/shortcut.service';
import { SolTabComponent } from '../sol-tab/sol-tab.component';
import { SolButtonType } from '../../../models/core/sol-button';
import { SolTab, SolTabsState } from './sol-tabs.state';

export type { SolTab } from './sol-tabs.state';

@Component({
  selector: 'sol-tabs',
  imports: [TranslatePipe, SolButtonComponent, SolTagComponent],
  templateUrl: './sol-tabs.component.html',
})
export class SolTabsComponent
  extends AbstractComponent<SolTabsState>
  implements OnInit, AfterContentInit, AfterViewInit, OnDestroy
{
  protected readonly SolIcon = SolIcon;
  protected readonly SolButtonType = SolButtonType;

  // Dependencies
  private readonly shortcutService = inject(ShortcutService);

  // Computed
  protected readonly visibleTabs$ = this.compute(state => state.visibleTabs);
  protected readonly previousVisible$ = this.compute(state => state.previousVisible);
  protected readonly nextVisible$ = this.compute(state => state.nextVisible);
  protected readonly dividerVisible$ = this.compute(state => state.dividerVisible);
  protected readonly contentVisible$ = this.compute(state => state.contentVisible);
  protected readonly large$ = this.compute(state => state.large);
  protected readonly hostClass$ = this.compute(state => state.hostClass);

  // Events
  @Output('selectedTab')
  private readonly selectedTabEmitter = new EventEmitter<string>();

  // Binding
  private readonly host = inject(ElementRef<HTMLElement>);
  @ViewChild('tabsScrollContainer')
  private tabsScrollContainerRef?: ElementRef;
  @ViewChild('tabs')
  private tabsRef?: ElementRef;
  @ViewChildren('tab')
  private tabRefs?: QueryList<ElementRef>;
  @ContentChildren(SolTabComponent)
  tabComponents?: QueryList<SolTabComponent>;

  // Cache
  private shortcutSubscription?: SolShortcutSubscription;
  private resizeObservers: ResizeObserver[] = [];
  private cachedTabsContainerWidth = -1;

  constructor() {
    super(SolTabsState.of());

    this.watch(() => this.updateScrollPosition());
    this.watchComputed(
      state => state.selectedTab,
      () => this.updateVisibleTabContent(),
    );
  }

  ngOnInit(): void {
    this.shortcutSubscription = this.shortcutService.register('sol-tabs', false, this.host, [
      {
        shortcut: SolShortcut.of('ArrowLeft'),
        action: () => this.onArrowLeft(),
      },
      {
        shortcut: SolShortcut.of('ArrowRight'),
        action: () => this.onArrowRight(),
      },
    ]);
  }

  ngAfterContentInit(): void {
    this.updateVisibleTabContent();
    this.tabComponents!.changes.subscribe(() => this.updateVisibleTabContent());
  }

  ngAfterViewInit(): void {
    this.observeResize(this.tabsScrollContainerRef, entries => {
      for (const entry of entries) {
        if (entry.contentRect) {
          this.update(state => state.withTabsContainerWidth(entry.contentRect.width));
        }
      }
    });
    this.observeResize(this.tabsRef, entries => {
      let tabsWidth = 0;
      for (const entry of entries) {
        if (entry.contentRect) {
          tabsWidth = entry.contentRect.width;
          break;
        }
      }

      const tabWidths: number[] = [];
      if (this.tabRefs) {
        for (let i = 0; i < this.tabRefs.length || 0; i++) {
          const ref = this.tabRefs.get(i);
          const tabWidth = ref?.nativeElement?.getBoundingClientRect().width;
          tabWidths.push(tabWidth);
        }
      }

      this.update(state => state.withTabsResize(tabsWidth, tabWidths));
    });
  }

  ngOnDestroy(): void {
    this.shortcutSubscription?.unsubscribe();
    this.resizeObservers.forEach(observer => observer.disconnect());
  }

  @HostBinding('class')
  protected get hostClass(): string {
    return this.hostClass$();
  }

  @Input()
  public set tabs(tabs: SolTab[]) {
    this.update(state => state.withTabs(tabs));
  }

  @Input()
  public set selectedTab(selectedTab: string) {
    this.update(state => state.withSelectedTab(selectedTab));
  }

  @Input()
  public set dividerVisible(dividerVisible: boolean) {
    this.update(state => state.withDividerVisible(dividerVisible));
  }

  @Input()
  public set large(large: boolean) {
    this.update(state => state.withLarge(large));
  }

  @Input()
  public set sizeToContent(sizeToContent: boolean) {
    this.update(state => state.withSizeToContent(sizeToContent));
  }

  protected selectTab(selectedTab: string) {
    // Already there
    if (this.state.selectedTab === selectedTab) {
      return;
    }

    // Update
    this.update(state => state.withSelectedTab(selectedTab));
    this.selectedTabEmitter.emit(selectedTab);
  }

  protected onPreviousOffscreenTab(): void {
    const state = this.state;
    const selectedTab =
      state.previousOffscreenTabIndex === -1 ? undefined : state.tabs[state.previousOffscreenTabIndex];
    if (selectedTab !== undefined) {
      this.selectTab(selectedTab.id);
    }
  }

  protected onNextOffscreenTab(): void {
    const state = this.state;
    const selectedTab = state.nextOffscreenTabIndex === -1 ? undefined : state.tabs[state.nextOffscreenTabIndex];
    if (selectedTab !== undefined) {
      this.selectTab(selectedTab.id);
    }
  }

  private updateVisibleTabContent() {
    if (!this.tabComponents) {
      this.update(state => state.withContentVisible(false));
      return;
    }

    const tabs = this.tabComponents.toArray();
    const selectedTab = this.state.selectedTab;
    for (const tab of tabs) {
      tab.visible = selectedTab === tab.id;
    }
    this.update(state => state.withContentVisible(tabs.length > 0));
  }

  private updateScrollPosition() {
    const state = this.state;
    const selectedIndex = state.selectedTabIndex;
    if (selectedIndex === -1 || !this.tabsScrollContainerRef) {
      return;
    }
    const scrollContainerEl = this.tabsScrollContainerRef.nativeElement as HTMLElement;
    const tabsContainerWidthChanged = this.cachedTabsContainerWidth !== state.tabsContainerWidth;
    this.cachedTabsContainerWidth = state.tabsContainerWidth;
    const behaviour = tabsContainerWidthChanged ? 'instant' : 'smooth';
    scrollContainerEl.scrollTo({ left: state.scrollLeft, behavior: behaviour });
  }

  private observeResize(ref: ElementRef | undefined, callback: (entries: ResizeObserverEntry[]) => void) {
    if (ref) {
      const el = ref.nativeElement;
      const observer = new ResizeObserver(callback);
      observer.observe(el);
      this.resizeObservers.push(observer);
    }
  }

  private onArrowLeft(): boolean {
    if (!this.hasFocusWithin()) return false;
    const state = this.state;
    const tabs = state.tabs;
    if (!state.selectedTab) return false;
    const index = tabs.findIndex(tab => tab.id === state.selectedTab);
    if (index <= 0) return false;
    this.selectTab(tabs[index - 1].id);
    return true;
  }

  private onArrowRight(): boolean {
    if (!this.hasFocusWithin()) return false;
    const state = this.state;
    const tabs = state.tabs;
    if (!state.selectedTab) return false;
    const index = tabs.findIndex(tab => tab.id === state.selectedTab);
    if (index < 0 || index >= tabs.length - 1) return false;
    this.selectTab(tabs[index + 1].id);
    return true;
  }

  /**
   * True only when keyboard focus is on a tab button inside the strip header.
   * Scoping to the `role="tablist"` element (not the whole host) prevents
   * Arrow-key navigation from stealing keystrokes typed into inputs, tables,
   * or any other interactive content rendered inside `<sol-tab>` panels.
   * Also bails when the active element is editable (e.g. a focused input
   * placed directly in the tab strip header itself).
   */
  private hasFocusWithin(): boolean {
    const active = document.activeElement;
    if (!active) return false;
    if (this.isEditable(active)) return false;
    const tabList = this.tabsRef?.nativeElement;
    if (!tabList) return false;
    return tabList.contains(active);
  }

  private isEditable(el: Element): boolean {
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    return el instanceof HTMLElement && el.isContentEditable;
  }
}
